import { Action, PermissionString, Subject } from '../permissions';
import { AnyPermissionString, Permission } from '../types';

/**
 * Parses a permission string and returns the subject, action, and conditions.
 * @param permissionString - The permission string to parse.
 * @returns The parsed permission string.
 */
export function parsePermissionString<S extends Subject, A extends Action<S>>(
  permissionString: PermissionString<S, A> | AnyPermissionString
): Permission<S, A> {
  // example format: "user:read"
  const [subjectWithType, actionWithConditions] = permissionString.split(':');
  const [action, conditionsString] = actionWithConditions.split('?');

  let type: 'can' | 'cannot' = 'can';
  let subject = subjectWithType;

  if (subjectWithType.startsWith('!')) {
    type = 'cannot';
    subject = subjectWithType.substring(1);
  }

  const conditions = Object.fromEntries(new URLSearchParams(conditionsString).entries());

  if (!subject || !action) {
    throw new Error(`Invalid permission string format: ${permissionString}`);
  }

  return { action: action.trim() as A, subject: subject.trim() as S, conditions, type };
}
